import React, { FC, useState } from 'react';
import { MutationFunction } from 'react-apollo';
import { useTranslation } from 'react-i18next';
import { Grid, Typography } from '@material-ui/core';

import { ClientFollowMapping, PublicUserProfile, UpdateClientFollowMappingComponent } from 'api';
import { UserPicker } from 'components/Pickers';
import { useNotify } from 'hooks';

interface IProps {
  mapping: ClientFollowMapping;
}

const UsersEdit: FC<IProps> = ({ mapping }) => {
  const [requiredSaving, setRequiredSaving] = useState(false);
  const [suggestedSaving, setSuggestedSaving] = useState(false);
  const notify = useNotify();

  const { t } = useTranslation();

  function editNotify() {
    const message = t('components:savedSuccessfully');
    notify.info({ message });
  }

  function getRequiredValues() {
    const { id, title, mapping_status, all_users } = mapping;
    return { id, title, mapping_status, all_users };
  }

  function handleRequiredUsersChange(users: PublicUserProfile[], mutation: MutationFunction) {
    setRequiredSaving(true);
    return mutation({
      variables: {
        data: {
          ...getRequiredValues(),
          required_user_ids: users.map(user => user.id),
        },
      },
      optimisticResponse: {
        updateClientFollowMapping: {
          ...mapping,
          required_users: users,
        },
      },
    }).then(
      () => setRequiredSaving(false),
      () => setRequiredSaving(false),
    );
  }

  function handleSuggestedUsersChange(users: PublicUserProfile[], mutation: MutationFunction) {
    setSuggestedSaving(true);
    return mutation({
      variables: {
        data: {
          ...getRequiredValues(),
          suggested_user_ids: users.map(user => user.id),
        },
      },
      optimisticResponse: {
        updateClientFollowMapping: {
          ...mapping,
          suggested_users: users,
        },
      },
    }).then(
      () => setSuggestedSaving(false),
      () => setSuggestedSaving(false),
    );
  }

  return (
    <Grid container>
      <Grid item xs={3}>
        <Typography variant="subtitle2">{t('common:person_plural')}</Typography>
      </Grid>
      <Grid item xs={9}>
        <UpdateClientFollowMappingComponent onError={notify.mutationError} onCompleted={editNotify}>
          {mutation => (
            <Grid container direction="column" spacing={4}>
              <Grid item>
                <Typography variant="subtitle2">{t('common:required')}</Typography>
                <Typography color="textSecondary">{t('admin:onboardingUsersRequired')}</Typography>
                <UserPicker
                  selectedUsers={mapping.required_users}
                  onSelectedUsersChange={(selectedUsers: PublicUserProfile[]) =>
                    handleRequiredUsersChange(selectedUsers, mutation)
                  }
                  saving={requiredSaving}
                />
              </Grid>
              <Grid item>
                <Typography variant="subtitle2">{t('common:suggested')}</Typography>
                <Typography color="textSecondary">{t('admin:onboardingUsersSuggested')}</Typography>
                <UserPicker
                  selectedUsers={mapping.suggested_users}
                  onSelectedUsersChange={(selectedUsers: PublicUserProfile[]) =>
                    handleSuggestedUsersChange(selectedUsers, mutation)
                  }
                  saving={suggestedSaving}
                  excludeIds={mapping.required_users.map(user => user.id)}
                />
              </Grid>
            </Grid>
          )}
        </UpdateClientFollowMappingComponent>
      </Grid>
    </Grid>
  );
};

export default UsersEdit;
